import { Tutorial } from '../types/tutorial';

/**
 * Main Onboarding Tutorial
 * This tutorial walks users through the key features of the app
 */
export const onboardingTutorial: Tutorial = {
  id: 'onboarding',
  name: 'Getting Started',
  description: 'Learn how to use Deconstructed to manage and sell your items',
  steps: [
    // Settings Page Steps
    {
      id: 'welcome',
      title: 'Welcome to Deconstructed!',
      description: "Let's take a quick tour to show you how everything works. We'll start with the Settings page to customize your experience.",
      position: 'center',
      route: '/settings',
    },
    {
      id: 'settings-ai-detection',
      title: 'AI Detection Settings',
      description: 'Configure how AI analyzes and categorizes your items. You can add custom prompts to focus on specific item types like construction materials or furniture.',
      target: '[data-tutorial="ai-detection"]',
      position: 'bottom',
      route: '/settings',
      highlightTarget: true,
    },
    {
      id: 'settings-multiple-items',
      title: 'Multiple Items Per Photo',
      description: 'Toggle this on to detect and create separate items for each object in a photo. For example, a photo with 3 chairs will create 3 separate items.',
      target: '[data-tutorial="multiple-items-toggle"]',
      position: 'bottom',
      route: '/settings',
      highlightTarget: true,
    },
    {
      id: 'settings-detection-model',
      title: 'Detection Model',
      description: 'Choose between faster or more accurate AI models. Select "Manual Only" for instant camera access without automatic detection.',
      target: '[data-tutorial="detection-model"]',
      position: 'bottom',
      route: '/settings',
      highlightTarget: true,
    },
    {
      id: 'settings-save',
      title: 'Save Your Settings',
      description: 'Don\'t forget to save your settings! They will be synced across all your devices.',
      target: '[data-tutorial="save-settings"]',
      position: 'top',
      route: '/settings',
      highlightTarget: true,
    },

    // Photos Page Steps
    {
      id: 'photos-intro',
      title: 'Taking Photos',
      description: "Now let's learn how to capture and manage photos. Photos are the foundation for creating items to sell.",
      position: 'center',
      route: '/photos',
    },
    {
      id: 'photos-take-photo',
      title: 'Take a Photo',
      description: 'Click this button to open the camera. You can take photos of items directly or upload from your device.',
      target: '[data-tutorial="take-photo"]',
      position: 'bottom',
      route: '/photos',
      highlightTarget: true,
    },
    {
      id: 'photos-voice-annotation',
      title: 'Voice Annotations',
      description: 'When taking a photo, you can add voice notes to provide context. The AI uses these notes to better understand and value your items.',
      position: 'center',
      route: '/photos',
    },
    {
      id: 'photos-select',
      title: 'Select Photos',
      description: 'Click the checkbox on any photo to select it. You can select multiple photos to create items in bulk.',
      position: 'center',
      route: '/photos',
    },
    {
      id: 'photos-create-items',
      title: 'Create Items from Photos',
      description: 'After selecting photos, click "Create Items" to automatically generate marketplace items with AI analysis and pricing.',
      position: 'center',
      route: '/photos',
    },

    // Items Page Steps
    {
      id: 'items-intro',
      title: 'Managing Items',
      description: "Let's explore the Items page where you can view, edit, and manage all your items.",
      position: 'center',
      route: '/items',
    },
    {
      id: 'items-manual-create',
      title: 'Create Item Manually',
      description: 'You can also create items manually by clicking this button. This is useful when you want to list items without photos or need more control.',
      target: '[data-tutorial="new-item"]',
      position: 'bottom',
      route: '/items',
      highlightTarget: true,
    },
    {
      id: 'items-view',
      title: 'Item Details',
      description: 'Click on any item to view its full details. You can see AI analysis, estimated values, and edit item information.',
      position: 'center',
      route: '/items',
    },
    {
      id: 'items-ai-analysis',
      title: 'AI Analysis',
      description: 'Each item created from a photo includes AI analysis with estimated values, confidence scores, and recommendations. This helps you price items competitively.',
      position: 'center',
      route: '/items',
    },
    {
      id: 'items-bulk-actions',
      title: 'Bulk Actions',
      description: 'Select multiple items to perform bulk actions like adding to projects, posting to listings, or deleting.',
      position: 'center',
      route: '/items',
    },

    // Final Step
    {
      id: 'complete',
      title: "You're All Set!",
      description: "That's it! You now know the basics of Deconstructed. Start taking photos and creating items to sell. You can always access this tutorial again from the Help button in the top menu.",
      position: 'center',
      route: '/items',
    },
  ],
};

/**
 * Quick Settings Tutorial
 * A shorter tutorial focused just on settings
 */
export const settingsTutorial: Tutorial = {
  id: 'settings-quick',
  name: 'Settings Guide',
  description: 'Learn how to configure your AI detection and item creation settings',
  steps: [
    {
      id: 'settings-intro',
      title: 'Settings Overview',
      description: 'Here you can customize how AI analyzes your photos and creates items. Let\'s walk through the key settings.',
      position: 'center',
      route: '/settings',
    },
    {
      id: 'settings-ai-detection',
      title: 'AI Detection Settings',
      description: 'Configure how AI analyzes and categorizes your items. You can add custom prompts to focus on specific item types.',
      target: '[data-tutorial="ai-detection"]',
      position: 'bottom',
      route: '/settings',
      highlightTarget: true,
    },
    {
      id: 'settings-multiple-items',
      title: 'Multiple Items Per Photo',
      description: 'Toggle this on to detect and create separate items for each object in a photo.',
      target: '[data-tutorial="multiple-items-toggle"]',
      position: 'bottom',
      route: '/settings',
      highlightTarget: true,
    },
    {
      id: 'settings-save',
      title: 'Save Your Settings',
      description: 'Don\'t forget to save your settings when you\'re done!',
      target: '[data-tutorial="save-settings"]',
      position: 'top',
      route: '/settings',
      highlightTarget: true,
    },
  ],
};

/**
 * Get tutorial by ID
 */
export const getTutorialById = (id: string): Tutorial | undefined => {
  const tutorials = [onboardingTutorial, settingsTutorial];
  return tutorials.find((t) => t.id === id);
};

/**
 * Get all available tutorials
 */
export const getAllTutorials = (): Tutorial[] => {
  return [onboardingTutorial, settingsTutorial];
};



